// Settings › Medical aid (design/screens/settings-medical-aid.html): the shared scheme → plan → option reference, read-only
// for every tenant (UC-PLATFORM-REFERENCE-006 step 5; INT-46, D30) from GET /reference/medical-aid/{schemes,plans,options}.
// The schemes table, the selected scheme's details and its plans with their options; the selection, the name filter and
// "Show discontinued" live in the URL. The prototype's tariff columns and "Coverage overrides in use" wait for the tariff
// tables and the pricing engine (Sprint 5), and "Add tenant-local scheme" for PAT-85 — none is drawn as if it worked; the
// page says what arrives when. No edit control: the reference is maintained by Vision X (UC-PLATFORM-REFERENCE-003).
import type { MedicalAidSchemeView } from "@visionx/contracts";
import { useQueries, useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router";
import { reference } from "../api/endpoints.patients";
import { formatDate } from "../lib/format";
import { Alert, Checkbox, Chip, Empty, ErrorAlert, KV, Loading, Panel } from "../ui/bits";
import { Icon } from "../ui/icons";
import { PageHead, SettingsTabs } from "./common";

const dash = <span className="faint">—</span>;
const SOURCE_LABEL: Record<MedicalAidSchemeView["source"], string> = { manual: "Manual capture", medprax: "Medprax feed" };

/** Active and in effect, Active but outside its window, or Discontinued with its date. */
function RefStatus({ row }: { row: { status: "Active" | "Discontinued"; inEffect: boolean; effectiveFrom: string; discontinuedDate: string | null } }) {
  if (row.status === "Discontinued") return <Chip tone="neutral">Discontinued{row.discontinuedDate ? ` ${formatDate(row.discontinuedDate)}` : ""}</Chip>;
  if (!row.inEffect) return <Chip tone="warn" dot>From {formatDate(row.effectiveFrom)}</Chip>;
  return <Chip tone="ok" dot>Current</Chip>;
}
const windowText = (from: string, to: string | null): string => (to ? `${formatDate(from)} – ${formatDate(to)}` : `from ${formatDate(from)}`);

export function SettingsMedicalAid() {
  const [sp, setSp] = useSearchParams();
  const discontinued = sp.get("discontinued") === "true";
  const filter = sp.get("q") ?? "";
  const opts = { includeDiscontinued: discontinued ? "true" as const : undefined };
  const schemes = useQuery({ queryKey: ["reference", "schemes", "settings", discontinued], queryFn: () => reference.schemes(opts), staleTime: 5 * 60_000 });
  const all = schemes.data?.data.items ?? [];
  const needle = filter.trim().toLowerCase();
  const shown = needle ? all.filter((s) => s.name.toLowerCase().includes(needle) || (s.schemeCode ?? "").toLowerCase().includes(needle)) : all;
  const selectedId = sp.get("scheme") ?? shown[0]?.id ?? null;
  const selected = all.find((s) => s.id === selectedId) ?? null;
  const plans = useQuery({ queryKey: ["reference", "plans", selected?.id, "settings", discontinued], queryFn: () => reference.plans(selected!.id, opts), enabled: !!selected, staleTime: 5 * 60_000 });
  const planRows = plans.data?.data.items ?? [];
  const options = useQueries({ queries: planRows.map((pl) => ({ queryKey: ["reference", "options", pl.id, "settings", discontinued], queryFn: () => reference.options(pl.id, opts), staleTime: 5 * 60_000 })) });
  const setParam = (k: string, v: string | null) => setSp((p) => { const n = new URLSearchParams(p); if (v) n.set(k, v); else n.delete(k); return n; }, { replace: true });

  return (
    <section className="screen" id="s-settings-medical-aid">
      <div className="content">
        <PageHead title="Medical aid — schemes, plans and options" sub="Global reference data · read-only · maintained by Vision X and shared by every practice" />
        <SettingsTabs />
        <Alert tone="info">
          The schemes, plans and options here are the ones the patient form and the kiosk offer. They are global reference data maintained by Vision X; a practice reads them and cannot change them.
          Tariff tables (exam, frame maximum, lens rates, the annual optical benefit) arrive with the pricing engine in Sprint 5, and a practice's own unverified scheme later; until then a membership records the scheme, plan and option only.
        </Alert>
        <div className="toolbar">
          <div className="input" style={{ width: 280 }}>
            <Icon name="search" />
            <input type="search" aria-label="Filter schemes" placeholder="Filter by scheme name or code…" value={filter} onChange={(e) => setParam("q", e.target.value || null)} />
          </div>
          <Checkbox checked={discontinued} onChange={(v) => setParam("discontinued", v ? "true" : null)}>Show discontinued</Checkbox>
          {schemes.data && <span className="right small faint">{shown.length} of {all.length} scheme{all.length === 1 ? "" : "s"}</span>}
        </div>
        {schemes.isPending && <Loading what="schemes" />}
        <ErrorAlert error={schemes.error} />
        {schemes.data && (
          <Panel title="Schemes" meta={`${all.length} scheme${all.length === 1 ? "" : "s"}${discontinued ? " · discontinued included" : ""}`} tight>
            {shown.length === 0 ? (
              <Empty title={all.length === 0 ? "No schemes are loaded yet." : `No scheme matches “${filter}”.`}>{all.length === 0 ? "Vision X loads the scheme reference; ask the platform team if the list stays empty." : "Clear the filter to see every scheme."}</Empty>
            ) : (
              <div className="tbl-scroll">
                <table>
                  <thead><tr><th>Scheme</th><th>Code</th><th>Registration no</th><th>Source</th><th>Effective</th><th>Status</th></tr></thead>
                  <tbody>
                    {shown.map((s) => (
                      <tr key={s.id} className={s.id === selected?.id ? "sel" : ""} aria-selected={s.id === selected?.id}>
                        <td className="nw"><Link to={`?${new URLSearchParams({ ...Object.fromEntries(sp), scheme: s.id }).toString()}`} replace aria-current={s.id === selected?.id ? "true" : undefined}>{s.name}</Link>{!s.isVerified && <> <Chip tone="warn">Unverified</Chip></>}</td>
                        <td className="mono">{s.schemeCode ?? dash}</td>
                        <td className="mono">{s.registrationNumber ?? dash}</td>
                        <td>{SOURCE_LABEL[s.source]}</td>
                        <td className="nw">{windowText(s.effectiveFrom, s.effectiveTo)}</td>
                        <td><RefStatus row={s} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        )}
        {selected && (
          <div className="grid-2">
            <Panel title={selected.name} meta="selected" head={<span className="right"><RefStatus row={selected} /></span>}>
              <KV rows={[
                ["Scheme code", selected.schemeCode ? <span className="mono">{selected.schemeCode}</span> : dash],
                ["Registration no", selected.registrationNumber ? <span className="mono">{selected.registrationNumber}</span> : dash],
                ["Contact", [selected.contactName, selected.phone, selected.email].filter(Boolean).join(" · ") || dash],
                ["Effective", windowText(selected.effectiveFrom, selected.effectiveTo)],
                ["Source", <>{SOURCE_LABEL[selected.source]}{selected.isVerified ? "" : " · unverified"}</>],
                ["Claims routing", [selected.hbRoutingCode ? `Healthbridge ${selected.hbRoutingCode}` : null, selected.mediswitchDestinationCode ? `MediSwitch ${selected.mediswitchDestinationCode}` : null].filter(Boolean).join(" · ") || <span className="faint">Electronic claims are on the roadmap</span>],
                ["Last updated", formatDate(selected.updatedAt)],
              ]} />
            </Panel>
            <Panel title="Plans and options" meta={plans.data ? `${planRows.length} plan${planRows.length === 1 ? "" : "s"}` : undefined} tight>
              {plans.isPending && <div className="panel-body"><Loading what="plans" /></div>}
              {plans.isError && <div className="panel-body"><ErrorAlert error={plans.error} /></div>}
              {plans.data && planRows.length === 0 && <Empty title="No plans loaded for this scheme." />}
              {planRows.length > 0 && (
                <table>
                  <thead><tr><th>Plan · option</th><th>Code</th><th>Status</th></tr></thead>
                  <tbody>
                    {planRows.map((pl, i) => {
                      const o = options[i];
                      const opts = o?.data?.data.items ?? [];
                      return [
                        <tr key={pl.id}><td className="strong">{pl.name}</td><td className="mono">{pl.planCode ?? dash}</td><td><RefStatus row={pl} /></td></tr>,
                        ...(o?.isPending ? [<tr key={`${pl.id}-loading`}><td colSpan={3}><Loading what="options" /></td></tr>] : []),
                        ...(o?.isError ? [<tr key={`${pl.id}-error`}><td colSpan={3}><ErrorAlert error={o.error} /></td></tr>] : []),
                        ...(o?.data && opts.length === 0 ? [<tr key={`${pl.id}-none`}><td colSpan={3} className="faint">No options</td></tr>] : []),
                        ...opts.map((op) => <tr key={op.id}><td style={{ paddingLeft: 28 }}>{op.name}</td><td className="mono">{op.optionCode ?? dash}</td><td><RefStatus row={op} /></td></tr>),
                      ];
                    })}
                  </tbody>
                </table>
              )}
            </Panel>
          </div>
        )}
      </div>
    </section>
  );
}
